// Admin-only server functions for Smart Bousala fulfilment (Block C).
//
// Every function here is admin-only (never staff/assistant). All reads and
// writes go through the service-role client AFTER the admin check, because the
// queue spans every buyer. Nothing logged here may contain a name, email,
// phone or id of a buyer.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { toWhatsAppLink } from "@/lib/whatsapp-link";
import { BOUSALA_PRODUCTS } from "@/lib/bousala/products";
import { tMajor, tScale } from "./i18n";
import { VALUE_LABEL } from "./registry";
import {
  SMART_SECTION_KEYS,
  SMART_SECTION_TITLES_AR,
  canGenerate,
  canRefundSmartOrder,
  isOverdue,
  type SmartOrderStatus,
  type SmartSectionKey,
} from "./smart-order";
import {
  asReportLanguage,
  smartCountryLabel,
  smartFinancialLabel,
  SMART_REPORT_LANGUAGE_LABELS,
  type SmartReportLanguage,
} from "./smart-intake-schema";
import { sortSmartQueue, needsActionCount, isGenerationStuck } from "./smart-queue";
import {
  runSmartGeneration,
  setSmartOrderStatus,
  appendAdminNote,
  loadSmartPromptInput,
  validateGeneration,
} from "./smart-generate-flow";
import { smartPromptVersion } from "./smart-prompt";
import { checkSmartRefund, smartRefundMessage, smartRefundNote } from "./smart-refund";
import { DEMO_INTAKE } from "./smart-demo-data";
import type { ScoreResult } from "./types";
import { siteOrigin } from "@/lib/site-origin.server";

// ---------- helpers ----------

export async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin")) throw new Error("Forbidden: admin only");
}

async function admin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

function firstName(full: string | null | undefined): string {
  return (full ?? "").trim().split(/\s+/)[0] ?? "";
}

async function generateDeps() {
  const { generateSmartSections } = await import("./smart-generate.server");
  return { generate: generateSmartSections };
}

// ---------- queue ----------

export type SmartQueueItem = {
  id: string;
  firstName: string;
  status: SmartOrderStatus;
  createdAt: string;
  slaDueAt: string | null;
  overdue: boolean;
  paymentMethod: string | null;
  environment: string | null;
};

export const listSmartOrdersAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();

    const { data: orders, error } = await db
      .from("smart_reports")
      .select("id, status, created_at, sla_due_at, client_id, purchase_id")
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) throw error;
    const rows = orders ?? [];

    const clientIds = [...new Set(rows.map((r: any) => r.client_id).filter(Boolean))];
    const purchaseIds = [...new Set(rows.map((r: any) => r.purchase_id).filter(Boolean))];

    const [{ data: profiles }, { data: purchases }] = await Promise.all([
      clientIds.length
        ? db.from("profiles").select("id, full_name").in("id", clientIds)
        : Promise.resolve({ data: [] as any[] }),
      purchaseIds.length
        ? db.from("purchases").select("id, payment_method, environment").in("id", purchaseIds)
        : Promise.resolve({ data: [] as any[] }),
    ]);
    const nameById = new Map((profiles ?? []).map((p: any) => [p.id, p.full_name]));
    const purchaseById = new Map((purchases ?? []).map((p: any) => [p.id, p]));

    const now = new Date();
    const items: SmartQueueItem[] = rows.map((r: any) => ({
      id: r.id,
      firstName: firstName(nameById.get(r.client_id)),
      status: r.status,
      createdAt: r.created_at,
      slaDueAt: r.sla_due_at,
      overdue: isOverdue({ status: r.status, slaDueAt: r.sla_due_at }, now),
      paymentMethod: purchaseById.get(r.purchase_id)?.payment_method ?? null,
      environment: purchaseById.get(r.purchase_id)?.environment ?? null,
    }));

    const sorted = sortSmartQueue(
      items.map((i) => ({ ...i, slaDueAt: i.slaDueAt, createdAt: i.createdAt })),
      now,
    );
    return { items: sorted, needsAction: needsActionCount(items) };
  });

// ---------- one order ----------

export type SmartOrderDetail = {
  id: string;
  status: SmartOrderStatus;
  createdAt: string;
  slaDueAt: string | null;
  sentAt: string | null;
  waSentAt: string | null;
  overdue: boolean;
  generationStuck: boolean;
  adminNotes: string;
  promptVersion: string | null;
  buyerFirstName: string;
  hasPhone: boolean;
  duplicateOrders: number;
  purchase: { status: string; paymentMethod: string | null; environment: string | null } | null;
  reportLanguage: SmartReportLanguage;
  intake: {
    reportLanguage: string;
    targetCountry: string;
    financialContext: string;
    familyExpectations: string;
    futureGoals: string;
    concerns: string;
    majors: string[];
  };
  results: {
    fingerprint: string;
    hollandCode: string;
    intelligences: string[];
    personality: Array<{ trait: string; band: string }>;
    skillsStrong: string[];
    skillsDevelop: string[];
    valuesTop: string[];
    readinessPct: number;
    flags: string[];
    confidence: string;
    majorsByTier: Record<string, Array<{ name: string; match: number }>>;
  } | null;
  canRebind: boolean;
  canRefund: boolean;
  canGenerate: boolean;
  generateBlockedReason: string | null;
  sections: Array<{ key: SmartSectionKey; title: string; body: string; editedAt: string | null }>;
};

async function loadOrderDetail(db: any, id: string): Promise<SmartOrderDetail> {
  const { data: order, error } = await db
    .from("smart_reports")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!order) throw new Error("Order not found");

  const [{ data: profile }, { data: purchase }, { data: dupes }, { data: sectionRows }] =
    await Promise.all([
      db.from("profiles").select("full_name, phone_whatsapp").eq("id", order.client_id).maybeSingle(),
      order.purchase_id
        ? db
            .from("purchases")
            .select("status, payment_method, environment")
            .eq("id", order.purchase_id)
            .maybeSingle()
        : Promise.resolve({ data: null }),
      db.from("smart_reports").select("id").eq("client_id", order.client_id),
      db
        .from("smart_report_sections")
        .select("section_key, body_md, edited_at")
        .eq("smart_report_id", id),
    ]);

  // intake majors
  const { data: picked } = await db
    .from("smart_report_majors")
    .select("major_id, sort_order")
    .eq("smart_report_id", id)
    .order("sort_order");
  const majorIds = (picked ?? []).map((r: any) => r.major_id);
  let majorNames: string[] = [];
  if (majorIds.length) {
    const { data: majors } = await db.from("majors").select("id, name_ar").in("id", majorIds);
    const byId = new Map((majors ?? []).map((m: any) => [m.id, m.name_ar]));
    majorNames = majorIds.map((mid: string) => byId.get(mid)).filter(Boolean) as string[];
  }

  // Bousala 1 results from the bound attempt
  let results: SmartOrderDetail["results"] = null;
  if (order.attempt_id) {
    const { data: attempt } = await db
      .from("attempts")
      .select("scores")
      .eq("id", order.attempt_id)
      .maybeSingle();
    const s = attempt?.scores as ScoreResult | undefined;
    if (s?.holland_code) {
      results = {
        fingerprint: s.fingerprint_name,
        hollandCode: s.holland_code,
        intelligences: (s.intelligences_top3 ?? []).map((sid) => tScale(sid, "ar")),
        personality: Object.entries(s.personality ?? {}).map(([sid, v]) => ({
          trait: tScale(sid, "ar"),
          band: v.band,
        })),
        skillsStrong: (s.skills_strong ?? []).map((x) => tScale(x.id, "ar")),
        skillsDevelop: (s.skills_develop ?? []).map((x) => tScale(x.id, "ar")),
        valuesTop: (s.values_top ?? []).map((v) => VALUE_LABEL[v] ?? v),
        readinessPct: s.readiness_pct ?? 0,
        flags: Object.entries(s.flags ?? {}).map(([fid, band]) => `${tScale(fid, "ar")}: ${band}`),
        confidence: s.partial ? "متوسطة" : "مرتفعة",
        majorsByTier: Object.fromEntries(
          (["very_suitable", "conditional", "explore", "not_preferred"] as const).map((tier) => [
            tier,
            (s.majors[tier] ?? []).map((m) => ({ name: tMajor(m.slug, "ar").name, match: m.match })),
          ]),
        ),
      };
    }
  }

  // Is there a newer scored attempt for this client than the bound one?
  let canRebind = false;
  if (order.status === "paid" || order.status === "stalled") {
    const latest = await latestScoredAttempt(db, order.client_id);
    canRebind = Boolean(latest && latest.id !== order.attempt_id);
  }

  const paymentVerified = purchase?.status === "paid" || purchase?.status === "granted";
  const gate = canGenerate({
    status: order.status,
    attemptId: order.attempt_id,
    paymentVerified,
  });

  const now = new Date();
  const bodyByKey = new Map<string, { body_md: string | null; edited_at: string | null }>(
    (sectionRows ?? []).map((r: any) => [r.section_key, r]),
  );

  return {
    id: order.id,
    status: order.status,
    createdAt: order.created_at,
    slaDueAt: order.sla_due_at,
    sentAt: order.sent_at,
    waSentAt: order.wa_sent_at,
    overdue: isOverdue({ status: order.status, slaDueAt: order.sla_due_at }, now),
    generationStuck: isGenerationStuck(
      { status: order.status, updatedAt: order.updated_at },
      now,
    ),
    adminNotes: order.admin_notes ?? "",
    promptVersion: order.prompt_version,
    buyerFirstName: firstName(profile?.full_name),
    hasPhone: Boolean(profile?.phone_whatsapp),
    duplicateOrders: (dupes ?? []).length,
    purchase: purchase
      ? {
          status: purchase.status,
          paymentMethod: purchase.payment_method,
          environment: purchase.environment,
        }
      : null,
    reportLanguage: asReportLanguage(order.report_language),
    intake: {
      reportLanguage: SMART_REPORT_LANGUAGE_LABELS[asReportLanguage(order.report_language)],
      targetCountry: order.target_country
        ? smartCountryLabel(order.target_country, "ar")
        : "—",
      financialContext: order.financial_context
        ? smartFinancialLabel(order.financial_context, "ar")
        : "—",
      familyExpectations: order.family_expectations ?? "",
      futureGoals: order.future_goals ?? "",
      concerns: order.concerns ?? "",
      majors: majorNames,
    },
    results,
    canRebind,
    canRefund: canRefundSmartOrder(order.status),
    canGenerate: gate.ok,
    generateBlockedReason: gate.ok ? null : (gate.reason ?? null),
    sections: SMART_SECTION_KEYS.map((key) => ({
      key,
      title: SMART_SECTION_TITLES_AR[key],
      body: bodyByKey.get(key)?.body_md ?? "",
      editedAt: bodyByKey.get(key)?.edited_at ?? null,
    })),
  };
}

async function latestScoredAttempt(db: any, clientId: string) {
  const { data } = await db
    .from("attempts")
    .select("id, scores, completed_at, created_at")
    .eq("client_id", clientId)
    .order("created_at", { ascending: false })
    .limit(10);
  return (data ?? []).find((a: any) => a?.scores?.holland_code) ?? null;
}

const idInput = (d: unknown) => z.object({ id: z.string().uuid() }).parse(d);

export const getSmartOrderAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(idInput)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    return loadOrderDetail(await admin(), data.id);
  });

export const saveSmartAdminNotes = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid(), notes: z.string().max(8000) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();
    const { error } = await db
      .from("smart_reports")
      .update({ admin_notes: data.notes })
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

export const rebindLatestAttempt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(idInput)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();

    const { data: order } = await db
      .from("smart_reports")
      .select("id, status, client_id, attempt_id, admin_notes")
      .eq("id", data.id)
      .maybeSingle();
    if (!order) throw new Error("Order not found");
    if (order.status !== "paid" && order.status !== "stalled") {
      throw new Error("لا يمكن إعادة الربط في هذه الحالة.");
    }
    const latest = await latestScoredAttempt(db, order.client_id);
    if (!latest) throw new Error("لا توجد محاولة مكتملة لهذا العميل.");
    if (latest.id === order.attempt_id) throw new Error("الطلب مربوط بآخر محاولة أصلاً.");

    await db.from("smart_reports").update({ attempt_id: latest.id }).eq("id", order.id);
    await setSmartOrderStatus(db, order.id, order.status, "assessment_complete");
    await appendAdminNote(db, order.id, order.admin_notes, "أُعيد الربط بآخر محاولة مكتملة.");
    return { ok: true };
  });

// ---------- generation ----------

export const generateSmartDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(idInput)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();
    const result = await runSmartGeneration(db, data.id, await generateDeps());
    if (!result.ok) console.warn("[smart-generate] failed", { reason: result.reason });
    return result;
  });

export const retrySmartDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(idInput)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();

    const { data: order } = await db
      .from("smart_reports")
      .select("id, status, updated_at, admin_notes")
      .eq("id", data.id)
      .maybeSingle();
    if (!order) throw new Error("Order not found");

    const now = new Date();
    if (order.status === "stalled") {
      await setSmartOrderStatus(db, order.id, "stalled", "intake_complete");
    } else if (
      order.status === "generating" &&
      isGenerationStuck({ status: "generating", updatedAt: order.updated_at }, now)
    ) {
      await setSmartOrderStatus(db, order.id, "generating", "stalled");
      await setSmartOrderStatus(db, order.id, "stalled", "intake_complete");
      await appendAdminNote(db, order.id, order.admin_notes, "توليد معلّق أكثر من عشر دقائق — أعيدت المحاولة.");
    } else {
      throw new Error("لا حاجة لإعادة المحاولة في هذه الحالة.");
    }

    return runSmartGeneration(db, order.id, await generateDeps());
  });

export const regenerateSmartSection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({ id: z.string().uuid(), key: z.enum(SMART_SECTION_KEYS as unknown as [string, ...string[]]) })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();
    const key = data.key as SmartSectionKey;

    const { data: order } = await db
      .from("smart_reports")
      .select(
        "id, status, attempt_id, target_country, financial_context, family_expectations, future_goals, concerns, report_language",
      )
      .eq("id", data.id)
      .maybeSingle();
    if (!order) throw new Error("Order not found");
    // Editing is only allowed while the report is under review — never after
    // it was sent to the buyer.
    if (order.status !== "ready_for_review") {
      throw new Error("لا يمكن التعديل إلا أثناء المراجعة.");
    }

    const { input, allowedMajors, lang } = await loadSmartPromptInput(db, order);
    const { generateSmartSection } = await import("./smart-generate.server");
    const body = await generateSmartSection(input, key, "", lang);
    const issues = validateGeneration(
      { sections: { [key]: body }, needsHumanAttention: false, needsHumanAttentionReason: "" },
      allowedMajors,
      lang,
    ).filter((i) => i.key === key);
    if (issues.length > 0) {
      return { ok: false as const, reason: issues.map((i) => i.reason).join("، ") };
    }

    const now = new Date().toISOString();
    const { data: existing } = await db
      .from("smart_report_sections")
      .select("id")
      .eq("smart_report_id", data.id)
      .eq("section_key", key)
      .maybeSingle();
    const row = {
      smart_report_id: data.id,
      section_key: key,
      body_md: body,
      prompt_version: smartPromptVersion(lang),
      generated_at: now,
    };
    if (existing?.id) await db.from("smart_report_sections").update(row).eq("id", existing.id);
    else await db.from("smart_report_sections").insert(row);

    return { ok: true as const, body };
  });

export const saveSmartSection = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        key: z.enum(SMART_SECTION_KEYS as unknown as [string, ...string[]]),
        body: z.string().min(1).max(20000),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();

    const { data: gate } = await db
      .from("smart_reports")
      .select("status")
      .eq("id", data.id)
      .maybeSingle();
    if (!gate) throw new Error("Order not found");
    if (gate.status !== "ready_for_review") {
      throw new Error("لا يمكن التعديل إلا أثناء المراجعة.");
    }

    const { data: existing } = await db
      .from("smart_report_sections")
      .select("id")
      .eq("smart_report_id", data.id)
      .eq("section_key", data.key)
      .maybeSingle();
    const patch = { body_md: data.body, edited_by: userId, edited_at: new Date().toISOString() };
    if (existing?.id) {
      await db.from("smart_report_sections").update(patch).eq("id", existing.id);
    } else {
      await db.from("smart_report_sections").insert({
        smart_report_id: data.id,
        section_key: data.key,
        prompt_version: smartPromptVersion("ar"),
        ...patch,
      });
    }
    return { ok: true };
  });

// ---------- send ----------

function smartReadyMessage(name: string, link: string, lang: SmartReportLanguage = "ar"): string {
  if (lang === "en") {
    return `Hi ${name}, your Smart Bousala report is ready and has been reviewed by Dr. Sara's team. You can read it here: ${link}`;
  }
  return `مرحبًا ${name}، تقريرك من «البوصلة الذكية» جاهز، وراجعه فريق د. سارة. تقدر تقرأه من هون: ${link}`;
}

export const approveAndSendSmartReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(idInput)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();

    const { data: order } = await db
      .from("smart_reports")
      .select("id, status, client_id, report_language")
      .eq("id", data.id)
      .maybeSingle();
    if (!order) throw new Error("Order not found");
    if (order.status !== "ready_for_review") throw new Error("التقرير ليس جاهزاً للمراجعة.");

    const { data: sections } = await db
      .from("smart_report_sections")
      .select("section_key, body_md")
      .eq("smart_report_id", order.id);
    const bodyByKey = new Map((sections ?? []).map((s: any) => [s.section_key, s.body_md]));
    const missing = SMART_SECTION_KEYS.filter((k) => !(bodyByKey.get(k) ?? "").trim());
    if (missing.length > 0) throw new Error(`أقسام ناقصة: ${missing.join("، ")}`);

    // Flip the status first, conditionally: a refund or a second click that
    // lands between the read above and this write must lose.
    const nowIso = new Date().toISOString();
    await setSmartOrderStatus(db, order.id, "ready_for_review", "sent");
    await db
      .from("smart_reports")
      .update({ reviewed_by: userId, reviewed_at: nowIso, sent_at: nowIso })
      .eq("id", order.id);

    return buildSendLink(db, order.id, order.client_id, asReportLanguage(order.report_language));
  });

async function buildSendLink(
  db: any,
  orderId: string,
  clientId: string,
  lang: SmartReportLanguage = "ar",
) {
  const { data: profile } = await db
    .from("profiles")
    .select("user_id, full_name, phone_whatsapp")
    .eq("id", clientId)
    .maybeSingle();
  if (!profile?.user_id) throw new Error("Profile has no user");
  const { data: userData } = await db.auth.admin.getUserById(profile.user_id);
  const email = userData?.user?.email ?? "";
  if (!email) throw new Error("User has no email");

  const origin = siteOrigin();
  const { data: linkData, error: linkErr } = await db.auth.admin.generateLink({
    type: "magiclink",
    email,
    options: { redirectTo: `${origin}/portal/smart` },
  });
  if (linkErr) throw linkErr;
  const magicLink = linkData?.properties?.action_link ?? "";
  const message = smartReadyMessage(firstName(profile.full_name), magicLink, lang);
  return {
    orderId,
    magicLink,
    message,
    waLink: toWhatsAppLink(profile.phone_whatsapp, message),
  };
}

export const resendSmartReportLink = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(idInput)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();
    const { data: order } = await db
      .from("smart_reports")
      .select("id, client_id, status, report_language")
      .eq("id", data.id)
      .maybeSingle();
    if (!order) throw new Error("Order not found");
    if (order.status !== "sent") throw new Error("لم يُرسل التقرير بعد.");
    return buildSendLink(db, order.id, order.client_id, asReportLanguage(order.report_language));
  });

// ---------- manual refund ----------
//
// Never calls Paddle. Card refunds are issued in the Paddle dashboard; this
// only records the outcome so access and the order state stay truthful.
export const refundSmartOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid(), reason: z.string().trim().min(3).max(500) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    return refundSmartOrderCore(await admin(), data);
  });

/** Core logic, exported for tests (server fns skip validators in Node). */
export async function refundSmartOrderCore(db: any, data: { id: string; reason: string }) {
  {
    const { data: order } = await db
      .from("smart_reports")
      .select("id, status, client_id, purchase_id, admin_notes")
      .eq("id", data.id)
      .maybeSingle();
    if (!order) throw new Error("Order not found");

    const check = checkSmartRefund({ status: order.status as SmartOrderStatus, reason: data.reason });
    if (!check.ok) return { ok: false as const, reason: check.reason };

    // Compare-and-set from the status we read: a concurrent send or generation
    // finishing must not be silently overwritten.
    await setSmartOrderStatus(db, order.id, order.status as SmartOrderStatus, "refunded");

    if (order.purchase_id) {
      await db.from("purchases").update({ status: "refunded" }).eq("id", order.purchase_id);
    }
    await appendAdminNote(db, order.id, order.admin_notes, smartRefundNote(data.reason));

    const { data: profile } = await db
      .from("profiles")
      .select("full_name, phone_whatsapp")
      .eq("id", order.client_id)
      .maybeSingle();
    const message = smartRefundMessage(firstName(profile?.full_name));
    return {
      ok: true as const,
      message,
      waLink: toWhatsAppLink(profile?.phone_whatsapp ?? "", message),
      hasPhone: Boolean(profile?.phone_whatsapp),
    };
  }
}

export const markSmartWhatsAppSent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(idInput)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();
    await db
      .from("smart_reports")
      .update({ wa_sent_at: new Date().toISOString() })
      .eq("id", data.id);
    return { ok: true };
  });

// ---------- tone preview (stores nothing) ----------

export const previewSmartGeneration = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        targetCountry: z.string().default(DEMO_INTAKE.target_country),
        financialContext: z.string().default(DEMO_INTAKE.financial_context),
        familyExpectations: z.string().default(DEMO_INTAKE.family_expectations),
        futureGoals: z.string().default(DEMO_INTAKE.future_goals),
        concerns: z.string().default(DEMO_INTAKE.concerns),
        consideredMajorIds: z
          .array(z.string().uuid())
          .max(3)
          .default([...DEMO_INTAKE.considered_major_ids]),
        lang: z.enum(["ar", "en"]).default("ar"),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context as any;
    await assertAdmin(supabase, userId);
    const db = await admin();

    const { data: profile } = await db
      .from("profiles")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (!profile?.id) return { ok: false as const, reason: "لا يوجد ملف شخصي لهذا الحساب." };

    const attempt = await latestScoredAttempt(db, profile.id);
    if (!attempt) {
      return { ok: false as const, reason: "لا توجد لديك محاولة «بوصلة د. سارة» مصححة بعد." };
    }

    let consideredMajors: Array<{ slug: string; name_ar: string }> = [];
    if (data.consideredMajorIds.length > 0) {
      const { data: majorRows } = await db
        .from("majors")
        .select("id, slug, name_ar")
        .in("id", data.consideredMajorIds);
      const byId = new Map((majorRows ?? []).map((m: any) => [m.id, m]));
      consideredMajors = data.consideredMajorIds
        .map((id: string) => byId.get(id))
        .filter(Boolean)
        .map((m: any) => ({ slug: m.slug, name_ar: m.name_ar }));
    }

    const { buildSmartPromptInput } = await import("./smart-prompt");
    const input = buildSmartPromptInput({
      scores: attempt.scores as ScoreResult,
      consideredMajors,
      targetCountryLabel: smartCountryLabel(data.targetCountry, data.lang),
      financialContextLabel: smartFinancialLabel(data.financialContext, data.lang),
      familyExpectations: data.familyExpectations,
      futureGoals: data.futureGoals,
      concerns: data.concerns,
      lang: data.lang,
    });

    const { generateSmartSections } = await import("./smart-generate.server");
    const out = await generateSmartSections(input, "", data.lang);
    return { ok: true as const, sections: out.sections, lang: data.lang };
  });

export const SMART_PRODUCT_SLUG = BOUSALA_PRODUCTS.smart.slug;
